'use client';

/**
 * IncomeOpportunityClient - Income Opportunity page matching target dark theme
 * Transparent backgrounds, subtle borders, precise spacing
 */

import React from 'react';
import { Gem, Star } from 'lucide-react';
import { ReferralStatsSection } from './ReferralStatsSection';
import { ReferralTimeRangeSelector } from './ReferralTimeRangeSelector';
import { TierBenefitsMap } from './TierBenefitsMap';
import { ReferredClientsTable } from './ReferredClientsTable';
import { PartnerDiscountCode } from './PartnerDiscountCode';
import type { ReferralDashboardData } from '../types';

interface IncomeOpportunityClientProps {
  data: ReferralDashboardData;
}

export function IncomeOpportunityClient({ data }: IncomeOpportunityClientProps) {
  const { stats, tiers, referredClients, referralCode } = data;

  return (
    <div className="space-y-8 px-6 lg:px-12 max-w-[1400px] mx-auto">
      {/* Page Header - F-Pattern Top Bar */}
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        {/* Left: Icon + Title */}
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/25">
            <Gem className="h-6 w-6 text-black" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black tracking-tighter text-zinc-900 dark:text-white">
                Income Opportunity
              </h1>
              <span className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 uppercase tracking-widest">
                Partner Program
              </span>
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              Scale your agency revenue by referring clients. Advance through tiers to unlock
              dedicated support and up to{' '}
              <span className="font-semibold text-amber-500">40% recurring commission</span>.
            </p>
          </div>
        </div>

        {/* Right: Rank Identity Bubble — horizontal scroller on mobile so it can never
            push the header off-screen (flex-shrink-0 kept its full intrinsic width at
            390px and clipped ~266px past the viewport edge); restored to the original
            wrapping/shrink behavior at sm+ */}
        <div className="flex items-center gap-6 flex-nowrap overflow-x-auto overflow-y-hidden scrollbar-hide max-w-full w-full rounded-[20px] bg-white/60 dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 px-[29px] py-[11px] sm:w-auto sm:flex-shrink-0 sm:flex-wrap sm:overflow-visible">
          {/* Star Icon Bubble */}
          <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200 dark:border-zinc-700/60 flex items-center justify-center flex-shrink-0">
            <Star className="h-5 w-5 text-zinc-500 dark:text-zinc-400" />
          </div>

          {/* Current Rank */}
          <div className="flex-shrink-0 whitespace-nowrap">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
              Current Rank
            </p>
            <p className="text-base font-black tracking-tight text-zinc-900 dark:text-white">
              {stats.currentTier.display_name.split(' ')[0]}
            </p>
          </div>

          {/* Earnings/Commission */}
          <div className="flex-shrink-0 whitespace-nowrap">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
              Earnings
            </p>
            <p className="text-base font-black tracking-tight text-amber-500">
              {stats.currentTier.commission_percent}%
            </p>
          </div>
        </div>
      </div>

      {/* Partner Discount Code */}
      <PartnerDiscountCode referralCode={referralCode} />

      {/* Time-range selector — governs the whole page (KPIs + client list) */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
          Time Range
        </span>
        <ReferralTimeRangeSelector active={stats.period.key} />
      </div>

      {/* Stats Section */}
      <ReferralStatsSection stats={stats} />

      {/* Tier Benefits Map */}
      <TierBenefitsMap tiers={tiers} currentTierName={stats.currentTier.name} />

      {/* Referred Clients Table */}
      <ReferredClientsTable clients={referredClients} />
    </div>
  );
}
